import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { api, type OffsetPage } from '@/lib/api';
import { hasMergeFieldPlaceholders } from '@/lib/messaging-merge-fields';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime } from '@/utils/format';

type Channel = 'push' | 'sms' | 'email';
type AudienceType =
  | 'whole_church'
  | 'department'
  | 'unit'
  | 'group'
  | 'committee'
  | 'auto_group'
  | 'council'
  | 'list'
  | 'manual'
  | 'filter';
type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'partially_failed'
  | 'cancelled'
  | 'failed';
type DeliveryStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'bounced' | 'opted_out';

export interface CampaignDetail {
  id: string;
  name: string;
  channels: Channel[];
  audienceType: AudienceType;
  audienceRef: string | null;
  audienceMemberIds: string[] | null;
  subject: string | null;
  body: string;
  templateId: string | null;
  status: CampaignStatus;
  scheduledAt: string | null;
  repeatInterval: 'none' | 'daily' | 'weekly' | 'monthly';
  repeatUntil: string | null;
  sentAt: string | null;
  resolvedCount: number;
  soleRecipientName: string | null;
  previewBody: string | null;
  previewRecipientName: string | null;
  deliveredCount: number;
  failedCount: number;
  createdAt: string;
}

interface DeliveryRow {
  id: string;
  memberId: string | null;
  channel: Channel;
  destination: string | null;
  status: DeliveryStatus;
  failureCode: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

const campaignStatusVariant = (status: CampaignStatus): BadgeVariant => {
  if (status === 'sent') return 'success';
  if (status === 'failed' || status === 'partially_failed') return 'danger';
  if (status === 'cancelled') return 'neutral';
  if (status === 'scheduled' || status === 'sending') return 'info';
  return 'neutral';
};

const deliveryStatusVariant = (status: DeliveryStatus): BadgeVariant => {
  if (status === 'delivered' || status === 'sent') return 'success';
  if (status === 'failed' || status === 'bounced' || status === 'opted_out') return 'danger';
  return 'info';
};

export const CampaignDetailDialog = ({
  campaignId,
  open,
  onOpenChange,
  onResend,
  onDeleteDraft,
  resending = false,
}: {
  campaignId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResend?: (campaign: CampaignDetail) => void;
  onDeleteDraft?: (campaign: CampaignDetail) => void;
  resending?: boolean;
}) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const [deliveryPage, setDeliveryPage] = useState(1);

  useEffect(() => {
    if (open) setDeliveryPage(1);
  }, [open, campaignId]);

  const detail = useQuery({
    queryKey: ['messaging', 'campaigns', campaignId],
    queryFn: () => api.get<CampaignDetail>(`/messaging/campaigns/${campaignId}`),
    enabled: open && Boolean(campaignId),
  });

  const deliveries = useQuery({
    queryKey: ['messaging', 'campaigns', campaignId, 'deliveries', deliveryPage],
    queryFn: () =>
      api.get<{ data: DeliveryRow[]; page: OffsetPage }>(
        `/messaging/campaigns/${campaignId}/deliveries`,
        { query: { page: deliveryPage, pageSize: 25 } },
      ),
    enabled: open && Boolean(campaignId),
    placeholderData: keepPreviousData,
  });

  const channelLabel = (channel: Channel) => {
    if (channel === 'email') return t('campaigns.channelEmail');
    if (channel === 'sms') return t('campaigns.channelSms');
    return t('campaigns.channelPush');
  };

  const audienceLabel = (audienceType: AudienceType) => {
    switch (audienceType) {
      case 'whole_church':
        return t('campaigns.audienceWholeChurch');
      case 'department':
        return t('campaigns.audienceDepartment');
      case 'unit':
        return t('campaigns.audienceUnit');
      case 'group':
        return t('campaigns.audienceGroup');
      case 'committee':
        return t('campaigns.audienceCommittee');
      case 'auto_group':
        return t('campaigns.audienceAutoGroup');
      case 'council':
        return t('campaigns.audienceCouncil');
      case 'list':
        return t('campaigns.audienceList');
      case 'manual':
        return t('campaigns.audienceManual');
      case 'filter':
        return t('campaigns.audienceFilter');
      default:
        return '—';
    }
  };

  const deliveryColumns: DataTableColumn<DeliveryRow>[] = [
    {
      id: 'destination',
      header: t('campaigns.detail.deliveries.columns.to'),
      cell: (row) => row.destination?.trim() || '—',
    },
    {
      id: 'channel',
      header: t('campaigns.detail.deliveries.columns.channel'),
      cell: (row) => channelLabel(row.channel),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('campaigns.detail.deliveries.columns.status'),
      cell: (row) => (
        <Badge variant={deliveryStatusVariant(row.status)}>
          {t(`campaigns.detail.deliveryStatus.${row.status}`)}
        </Badge>
      ),
    },
  ];

  const campaign = detail.data;
  const page = deliveries.data?.page;
  const messageUsesPlaceholders = campaign ? hasMergeFieldPlaceholders(campaign.body) : false;
  const displayBody =
    campaign && messageUsesPlaceholders && campaign.previewBody
      ? campaign.previewBody
      : campaign?.body;
  const previewLabel =
    campaign?.previewRecipientName && messageUsesPlaceholders
      ? campaign.resolvedCount === 1
        ? t('campaigns.messagePreviewFor', { name: campaign.previewRecipientName })
        : t('campaigns.messagePreviewExample', {
            name: campaign.previewRecipientName,
            total: campaign.resolvedCount,
          })
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>
            {campaign?.name?.trim() || t('campaigns.detail.dialogTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {detail.isLoading || !campaign ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <div className="space-y-6">
              <dl>
                <DetailRow label={t('campaigns.status')}>
                  <Badge variant={campaignStatusVariant(campaign.status)}>
                    {t(`campaigns.statusLabel.${campaign.status}`)}
                  </Badge>
                </DetailRow>
                <DetailRow label={t('campaigns.channels')}>
                  {campaign.channels.map(channelLabel).join(' · ') || '—'}
                </DetailRow>
                <DetailRow label={t('campaigns.audience')}>
                  {audienceLabel(campaign.audienceType)}
                </DetailRow>
                <DetailRow label={t('campaigns.columns.recipients')}>
                  {campaign.soleRecipientName?.trim() ||
                    t('campaigns.recipients', { count: campaign.resolvedCount })}
                </DetailRow>
                <DetailRow label={t('campaigns.columns.delivery')}>
                  {t('campaigns.delivered', {
                    delivered: campaign.deliveredCount,
                    failed: campaign.failedCount,
                  })}
                </DetailRow>
                <DetailRow label={t('campaigns.detail.sentAt')}>
                  {campaign.sentAt
                    ? formatDateTime(campaign.sentAt, { locale, timezone })
                    : campaign.scheduledAt
                      ? t('campaigns.detail.scheduledFor', {
                          date: formatDateTime(campaign.scheduledAt, { locale, timezone }),
                        })
                      : '—'}
                </DetailRow>
                {campaign.repeatInterval !== 'none' && campaign.repeatUntil ? (
                  <DetailRow label={t('campaigns.repeat')}>
                    {t('campaigns.detail.repeats', {
                      interval: t(`campaigns.detail.repeatIntervals.${campaign.repeatInterval}`),
                      date: formatDate(campaign.repeatUntil, { locale }),
                    })}
                  </DetailRow>
                ) : null}
                {campaign.subject?.trim() ? (
                  <DetailRow label={t('campaigns.subject')}>{campaign.subject}</DetailRow>
                ) : null}
                <DetailRow label={t('campaigns.body')}>
                  <div className="space-y-1">
                    {previewLabel ? (
                      <p className="text-caption text-fg-secondary">{previewLabel}</p>
                    ) : null}
                    <p className="whitespace-pre-wrap break-words">{displayBody}</p>
                  </div>
                </DetailRow>
              </dl>

              <div className="space-y-2">
                <h3 className="text-body font-medium text-fg">
                  {t('campaigns.detail.deliveries.title')}
                </h3>
                <DataTable
                  rows={deliveries.data?.data ?? []}
                  columns={deliveryColumns}
                  rowKey={(row) => row.id}
                  loading={deliveries.isLoading}
                  empty={
                    <p className="text-center text-body-sm text-fg-secondary">
                      {t('campaigns.detail.deliveries.empty')}
                    </p>
                  }
                />
                {page && page.totalItems > 0 ? (
                  <Pagination
                    page={page.page}
                    pageSize={page.pageSize}
                    totalItems={page.totalItems}
                    totalPages={page.totalPages}
                    onPageChange={setDeliveryPage}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                    disabled={deliveries.isFetching}
                  />
                ) : null}
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter className="justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
          {campaign?.status === 'draft' && onDeleteDraft ? (
            <Button variant="secondary" onClick={() => onDeleteDraft(campaign)}>
              {t('campaigns.delete')}
            </Button>
          ) : null}
          {campaign && onResend ? (
            <Button disabled={resending} onClick={() => onResend(campaign)}>
              {campaign.status === 'draft' ? t('campaigns.send') : t('campaigns.resend')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
